import {
    PaymentAttemptResponseParams,
    PaymentCategory,
    PaymentRequestMode,
} from '../../../common/payment/Payment';
import { Language } from 'dg-web-shared/lib/Localized';
import {
    derivePaymentResponseState,
    DirectToAliasResponse,
    DirectToAliasResponseTag,
    ErrorResponse,
    handlePaymentAttemptResponse,
    ResponseState,
} from './PaymentBase';
import { useUpdate } from 'dg-web-shared/lib/Flux';
import {
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { UiPaymentOrigin } from 'dg-web-shared/common/utils/TwintPairingParkingpay';
import { useRef } from 'react';

export namespace TopUpPaymentRequest {
    interface TopUpPaymentPayloadBase {
        amount: number;
        language: Language;
        paymentCategory: PaymentCategory;
        uiPaymentOrigin: UiPaymentOrigin;
        ticketId?: string;
    }

    type DirectToAliasPayload = TopUpPaymentPayloadBase;

    interface NoOrExpiredPayload extends TopUpPaymentPayloadBase {
        useAlias: boolean;
    }

    export interface PaymentRequestPayload extends TopUpPaymentPayloadBase {
        mode: PaymentRequestMode;
    }

    export function use(): [ResponseState, (p: PaymentRequestPayload) => void] {
        const update = useUpdate();

        const noOrExpiredPayloadRef = useRef<NoOrExpiredPayload | null>(null);
        const directToAliasPayloadRef = useRef<DirectToAliasPayload | null>(
            null,
        );

        const [noOrExpiredAlias, sendNoOrExpiredAliasRequest] = useServerWrite<
            NoOrExpiredPayload & { baseUri: string },
            PaymentAttemptResponseParams,
            ErrorResponse
        >(() => ({
            url: '/ui-api/customer-account/deposits/deposit-attempt',
        }));

        const sendNoOrExpiredAlias = (p: NoOrExpiredPayload) => {
            noOrExpiredPayloadRef.current = p;
            sendNoOrExpiredAliasRequest({
                amount: p.amount,
                uiPaymentOrigin: p.uiPaymentOrigin,
                paymentCategory: p.paymentCategory,
                baseUri: `${window.location.protocol}//${window.location.host}`,
                language: p.language,
                ticketId: p.ticketId,
                useAlias: p.useAlias,
            });
        };

        useServerSuccessEffect(noOrExpiredAlias, () => {
            const payload = noOrExpiredPayloadRef.current;
            if (!payload) {
                return;
            }
            handlePaymentAttemptResponse(
                update,
                noOrExpiredAlias,
                payload,
                'top-up',
            );
        });

        const [directToAlias, sendDirectToAliasRequest] = useServerWrite<
            Pick<DirectToAliasPayload, 'amount' | 'uiPaymentOrigin'>,
            DirectToAliasResponse,
            ErrorResponse
        >(() => ({
            url: '/ui-api/customer-account/epaydirect/top-up',
        }));

        const sendDirectToAlias = (p: DirectToAliasPayload) => {
            directToAliasPayloadRef.current = p;
            sendDirectToAliasRequest({
                amount: p.amount,
                uiPaymentOrigin: p.uiPaymentOrigin,
            });
        };

        useServerSuccessEffect(directToAlias, response => {
            if (
                response.tag === DirectToAliasResponseTag.FAILURE_DATATRANS_API
            ) {
                const payload = directToAliasPayloadRef.current;
                if (!payload) {
                    return;
                }
                if (payload.paymentCategory !== PaymentCategory.TWINT) {
                    sendNoOrExpiredAlias({
                        paymentCategory: payload.paymentCategory,
                        amount: payload.amount,
                        useAlias: true,
                        language: payload.language,
                        uiPaymentOrigin: payload.uiPaymentOrigin,
                        ticketId: payload.ticketId,
                    });
                }
            }
        });

        return [
            derivePaymentResponseState(directToAlias, noOrExpiredAlias),
            (p: PaymentRequestPayload) => {
                if (p.mode === PaymentRequestMode.DIRECT_TO_ALIAS) {
                    sendDirectToAlias({
                        amount: p.amount,
                        uiPaymentOrigin: p.uiPaymentOrigin,
                        paymentCategory: p.paymentCategory,
                        language: p.language,
                        ticketId: p.ticketId,
                    });
                } else {
                    sendNoOrExpiredAlias({
                        amount: p.amount,
                        language: p.language,
                        uiPaymentOrigin: p.uiPaymentOrigin,
                        paymentCategory: p.paymentCategory,
                        useAlias: p.mode === PaymentRequestMode.INVALID_ALIAS,
                        ticketId: p.ticketId,
                    });
                }
            },
        ];
    }
}
